import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV, CONTACT } from "../data/site";
import { Wordmark } from "./Wordmark";
import { Socials } from "./Socials";
import { ArrowButton } from "./Buttons";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const main = NAV.slice(0, 7);

  // Routes whose header sits on a light (cream) background at the top.
  const p = location.pathname;
  const lightTopRoutes = ["/about", "/services", "/process", "/clients"];
  const darkTop = !lightTopRoutes.includes(p);
  const lightNav = darkTop && !scrolled; // use cream text/icons

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-cream/95 backdrop-blur-md border-b border-dark-text/10" : "bg-transparent"
        }`}
      >
        {/* Top utility bar: Follow us + socials (always visible) */}
        <div
          data-testid="header-social-bar"
          className={`border-b ${lightNav ? "border-cream/15" : "border-dark-text/10"}`}
        >
          <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-2 flex items-center justify-between">
            <Socials
              variant={lightNav ? "plainLight" : "plainDark"}
              showLabel
              iconSize="w-[17px] h-[17px]"
            />
            <a
              href={`mailto:${CONTACT.email}`}
              data-testid="header-email-link"
              className={`hidden sm:inline font-mono text-[10px] tracking-[0.2em] uppercase transition-colors ${
                lightNav ? "text-cream/65 hover:text-cream" : "text-dark-text/55 hover:text-bottle-green"
              }`}
            >
              {CONTACT.email}
            </a>
          </div>
        </div>

        {/* Main navigation row */}
        <div className={`transition-all duration-500 ${scrolled ? "py-3" : "py-4"}`}>
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 flex items-center justify-between">
          <Wordmark light={lightNav} />

          <nav className="hidden lg:flex items-center gap-7">
            {main.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-sans text-[13px] tracking-wide transition-colors duration-300 link-underline ${
                    lightNav
                      ? isActive ? "text-cream font-medium" : "text-cream/75 hover:text-cream"
                      : isActive ? "text-bottle-green font-medium" : "text-dark-text/70 hover:text-bottle-green"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <ArrowButton
              to="/contact"
              testid="nav-cta-contact"
              variant={lightNav ? "light" : "solid"}
              className="!px-5 !py-3 !text-xs"
            >
              Let's Talk
            </ArrowButton>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden p-1 transition-colors ${lightNav ? "text-cream" : "text-bottle-green"}`}
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-bottle-green lg:hidden flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-1">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05 }}
                >
                  <NavLink
                    to={item.path}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-serif text-4xl md:text-5xl text-cream/90 hover:text-cream py-2 block"
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-12 pt-8 border-t border-cream/15"
            >
              <Socials variant="boxLight" showLabel />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
